import { useState, useEffect, useCallback, useRef } from 'react';
import { useTeamStore } from '../store/teamStore';
import { toast } from 'react-hot-toast';
import type { 
  Team, AgeLevel, ExperienceLevel, Sport
} from '../types';
import { SportFormats } from '../types';

interface SettingsFormState {
  // Team information
  name: string;
  sport: Sport;
  ageLevel: AgeLevel;
  experienceLevel: ExperienceLevel;
  rosterSize: number;
  format: string;
  
  // Generic scheme fields for all sports
  offensiveScheme?: string;
  defensiveScheme?: string;
  
  // Baseball/Softball specific
  innings?: number;
  
  // Practice preferences
  defaultPracticeStartTime: string;
  defaultPracticeEndTime: string;
  defaultPracticeLocation: string;
  defaultDrillStepsPerDrill: number;
  aiUsePositionGroups: boolean;
  
  // Display settings
  timeFormat: '12h' | '24h';
  playerSortBy: 'firstName' | 'lastName' | 'number';
  
  // Feature toggles
  enableRandomization: boolean;
}

export const useSettings = () => {
  const { currentTeam, updateTeam } = useTeamStore();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [, setRerender] = useState(0);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isUpdatingRef = useRef(false);
  const isInitialLoadRef = useRef(true);
  const currentTeamIdRef = useRef<string | null>(null);
  const isInitializedRef = useRef(false);
  
  const forceUpdate = () => setRerender(x => x + 1);
  
  const [formState, setFormState] = useState<SettingsFormState>({
    name: '',
    sport: 'Football',
    ageLevel: '13U',
    experienceLevel: 'Intermediate',
    rosterSize: 22,
    format: '11v11',
    defaultPracticeStartTime: '15:00',
    defaultPracticeEndTime: '17:00',
    defaultPracticeLocation: 'Main Field',
    defaultDrillStepsPerDrill: 3,
    aiUsePositionGroups: true,
    timeFormat: '12h',
    playerSortBy: 'firstName',
    enableRandomization: false
  });

  // Auto-save function with debouncing - using useRef to prevent recreation
  const saveSettings = useRef(async (newFormState: SettingsFormState) => {
    console.log('[useSettings] Save attempt - currentTeam:', !!currentTeam, 'isInitialized:', isInitializedRef.current, 'isUpdating:', isUpdatingRef.current);
    
    if (!currentTeam || !isInitializedRef.current || isUpdatingRef.current) {
      console.log('[useSettings] Skipping save - not initialized, no team, or already updating');
      if (!currentTeam) console.log('[useSettings] Skip reason: No current team');
      if (!isInitializedRef.current) console.log('[useSettings] Skip reason: Not initialized');
      if (isUpdatingRef.current) console.log('[useSettings] Skip reason: Already updating');
      return;
    }

    console.log('[useSettings] Auto-saving settings...');
    setIsUpdating(true);
    isUpdatingRef.current = true;
    
    try {
      console.log('[useSettings] currentTeam.sport:', currentTeam.sport);
      console.log('[useSettings] currentTeam object:', currentTeam);
      
      const updateData: Partial<Team> = {
        id: currentTeam.id,
        name: newFormState.name,
        sport: currentTeam.sport, // Always include sport so updateTeam knows which sport-specific fields to handle
        ageLevel: newFormState.ageLevel,
        experienceLevel: newFormState.experienceLevel,
        rosterSize: newFormState.rosterSize,
        format: newFormState.format,
        offensiveScheme: newFormState.offensiveScheme,
        defensiveScheme: newFormState.defensiveScheme,
        innings: newFormState.innings,
        defaultPracticeStartTime: newFormState.defaultPracticeStartTime,
        defaultPracticeEndTime: newFormState.defaultPracticeEndTime,
        defaultPracticeLocation: newFormState.defaultPracticeLocation,
        defaultDrillStepsPerDrill: newFormState.defaultDrillStepsPerDrill,
        aiUsePositionGroups: newFormState.aiUsePositionGroups,
        timeFormat: newFormState.timeFormat,
        playerSortBy: newFormState.playerSortBy,
        enableRandomization: newFormState.enableRandomization
      };
      
      console.log('[useSettings] updateData being sent to teamStore:', updateData);
      console.log('[useSettings] updateData.sport:', updateData.sport);
      console.log('[useSettings] updateData.innings:', updateData.innings);
      
      const result = await updateTeam(updateData);
      console.log('[useSettings] Update team result:', result);
      
      setLastSaved(new Date());
      
      // Show subtle success indicator
      toast.success('Settings saved', { 
        duration: 2000,
        style: {
          background: '#10B981',
          color: 'white',
          fontSize: '14px',
          padding: '8px 12px'
        }
      });
    } catch (error) {
      console.error('Error auto-saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setIsUpdating(false);
      isUpdatingRef.current = false;
    }
  });

  const debouncedSave = useCallback((newFormState: SettingsFormState) => {
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout for auto-save
    saveTimeoutRef.current = setTimeout(() => {
      saveSettings.current(newFormState);
    }, 1000); // 1 second delay for auto-save
  }, []);

  useEffect(() => {
    const teamId = currentTeam?.id || null;
    
    // Only reset initialization if this is actually a different team
    if (teamId !== currentTeamIdRef.current) {
      console.log('[useSettings] Team changed from', currentTeamIdRef.current, 'to', teamId);
      setIsLoading(true);
      isInitializedRef.current = false;
      isInitialLoadRef.current = true;
      currentTeamIdRef.current = teamId;
    } else if (teamId && teamId === currentTeamIdRef.current) {
      console.log('[useSettings] Same team data received, skipping re-initialization');
      return; // Same team, don't re-initialize
    }
    
    if (currentTeam) {
      console.log('[useSettings] Team data received:', currentTeam);
      console.log('[useSettings] Team enableRandomization:', currentTeam.enableRandomization);
      
      // Initialize form with current team data
      const newFormState = {
        name: currentTeam.name || '',
        sport: currentTeam.sport || 'Football',
        ageLevel: currentTeam.ageLevel || '13U',
        experienceLevel: currentTeam.experienceLevel || 'Intermediate',
        rosterSize: currentTeam.rosterSize || 22,
        format: currentTeam.format || SportFormats[currentTeam.sport || 'Football'][0],
        
        // Generic scheme fields
        offensiveScheme: currentTeam.offensiveScheme,
        defensiveScheme: currentTeam.defensiveScheme,
        
        innings: currentTeam.innings,
        
        defaultPracticeStartTime: currentTeam.defaultPracticeStartTime || '15:00',
        defaultPracticeEndTime: currentTeam.defaultPracticeEndTime || '17:00',
        defaultPracticeLocation: currentTeam.defaultPracticeLocation || 'Main Field',
        defaultDrillStepsPerDrill: currentTeam.defaultDrillStepsPerDrill || 3,
        aiUsePositionGroups: currentTeam.aiUsePositionGroups ?? true,
        timeFormat: (currentTeam.timeFormat as '12h' | '24h') || '12h',
        playerSortBy: (currentTeam.playerSortBy as 'firstName' | 'lastName' | 'number') || 'firstName',
        enableRandomization: currentTeam.enableRandomization || false
      };
      
      console.log('[useSettings] Setting form state with team data');
      setFormState(newFormState);
      setIsLoading(false);
      
      // Set initialized flag after a brief delay to ensure form state is set
      setTimeout(() => {
        console.log('[useSettings] Form initialized, enabling auto-save');
        isInitializedRef.current = true;
        // After initialization is complete, mark that we're no longer in initial load
        setTimeout(() => {
          isInitialLoadRef.current = false;
          console.log('[useSettings] Initial load complete, auto-save now enabled for user changes');
        }, 50);
      }, 100);
    } else {
      console.log('[useSettings] No team data available yet');
    }
  }, [currentTeam]); // Changed back to full currentTeam dependency but with proper tracking

  // Auto-save when form state changes (but only after initialization and not during initial load)
  useEffect(() => {
    if (isInitializedRef.current && !isLoading && currentTeam && !isInitialLoadRef.current) {
      console.log('[useSettings] Form state changed by user, triggering auto-save');
      debouncedSave(formState);
    } else if (isInitialLoadRef.current) {
      console.log('[useSettings] Skipping auto-save - initial load in progress');
    }
  }, [formState, isLoading, debouncedSave]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (field: keyof SettingsFormState, value: string | number) => {
    console.log(`[useSettings] Field ${field} changed to:`, value);
    setFormState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTimeFormatChange = (format: '12h' | '24h') => {
    console.log('[useSettings] Time format changed to:', format);
    setFormState(prev => ({
      ...prev,
      timeFormat: format
    }));
  };

  const handlePlayerSortChange = (sortBy: 'firstName' | 'lastName' | 'number') => {
    console.log('[useSettings] Player sort changed to:', sortBy);
    setFormState(prev => ({
      ...prev,
      playerSortBy: sortBy
    }));
  };

  const handleToggleChange = (field: string, value: boolean) => {
    console.log(`[useSettings] Toggle ${field} changed to:`, value);
    setFormState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Keep the manual save function for backwards compatibility (if needed)
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    // Force immediate save by clearing timeout and saving directly
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    await saveSettings.current(formState);
  };

  return {
    currentTeam,
    formState,
    isLoading,
    isUpdating,
    lastSaved,
    forceUpdate,
    handleInputChange,
    handleTimeFormatChange,
    handlePlayerSortChange,
    handleToggleChange,
    handleSubmit
  };
}; 